/**
 * Pruefungen fuer deutsche Geschaeftsdaten.
 *
 * Alle Funktionen geben `null` zurueck, wenn der Wert in Ordnung ist, und
 * sonst eine Meldung fuer die Oberflaeche. Leere Eingaben gelten als
 * gueltig - ob ein Feld ausgefuellt sein muss, entscheidet das Formular.
 */
/**
 * IBAN nach ISO 13616 mit Pruefsumme (Modulo 97).
 *
 * Die Laenge ist je Land unterschiedlich; geprueft wird gegen die
 * Laendertabelle, damit eine um eine Stelle zu kurze deutsche IBAN nicht
 * durchrutscht.
 */
const IBAN_LENGTHS = {
    AT: 20, BE: 16, CH: 21, CZ: 24, DE: 22, DK: 18, ES: 24, FI: 18, FR: 27,
    GB: 22, HU: 28, IE: 22, IT: 27, LI: 21, LU: 20, NL: 18, NO: 15, PL: 28,
    PT: 25, SE: 24, SI: 19, SK: 24,
};
export function normalizeIban(value) {
    return value.replace(/\s/g, '').toUpperCase();
}
export function formatIban(value) {
    return normalizeIban(value).replace(/(.{4})/g, '$1 ').trim();
}
export function validateIban(value) {
    const iban = normalizeIban(value);
    if (iban === '')
        return null;
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban)) {
        return 'Die IBAN hat kein gueltiges Format.';
    }
    const country = iban.slice(0, 2);
    const expectedLength = IBAN_LENGTHS[country];
    if (expectedLength && iban.length !== expectedLength) {
        return `Eine IBAN aus ${country} hat ${expectedLength} Stellen, eingegeben wurden ${iban.length}.`;
    }
    // Die ersten vier Zeichen ans Ende, Buchstaben zu Zahlen, Rest bei 97
    // muss 1 sein. Die Zahl ist zu gross fuer Number, daher stueckweise.
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const digits = rearranged.replace(/[A-Z]/g, (char) => String(char.charCodeAt(0) - 55));
    let remainder = 0;
    for (const digit of digits) {
        remainder = (remainder * 10 + Number(digit)) % 97;
    }
    return remainder === 1 ? null : 'Die IBAN ist nicht korrekt (Pruefsumme stimmt nicht).';
}
/** BIC nach ISO 9362. */
export function validateBic(value) {
    const bic = value.replace(/\s/g, '').toUpperCase();
    if (bic === '')
        return null;
    return /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(bic)
        ? null
        : 'Der BIC hat kein gueltiges Format (8 oder 11 Zeichen).';
}
/** Laendercodes und Formate der USt-IdNr. im EU-Raum. */
const VAT_ID_PATTERNS = {
    AT: /^ATU\d{8}$/, BE: /^BE[01]\d{9}$/, BG: /^BG\d{9,10}$/, CY: /^CY\d{8}[A-Z]$/,
    CZ: /^CZ\d{8,10}$/, DE: /^DE\d{9}$/, DK: /^DK\d{8}$/, EE: /^EE\d{9}$/,
    ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/, FI: /^FI\d{8}$/, FR: /^FR[A-Z0-9]{2}\d{9}$/,
    GR: /^EL\d{9}$/, HR: /^HR\d{11}$/, HU: /^HU\d{8}$/, IE: /^IE[A-Z0-9]{8,9}$/,
    IT: /^IT\d{11}$/, LT: /^LT(\d{9}|\d{12})$/, LU: /^LU\d{8}$/, LV: /^LV\d{11}$/,
    MT: /^MT\d{8}$/, NL: /^NL\d{9}B\d{2}$/, PL: /^PL\d{10}$/, PT: /^PT\d{9}$/,
    RO: /^RO\d{2,10}$/, SE: /^SE\d{12}$/, SI: /^SI\d{8}$/, SK: /^SK\d{10}$/,
};
export function normalizeVatId(value) {
    return value.replace(/[\s.-]/g, '').toUpperCase();
}
/**
 * Format der USt-IdNr.
 *
 * Geprueft wird nur die Schreibweise, bei deutschen Nummern zusaetzlich die
 * Pruefziffer. Ob die Nummer tatsaechlich vergeben und dem Kunden zugeordnet
 * ist, kann nur eine Abfrage beim BZSt beantworten - die ist bei
 * innergemeinschaftlichen Lieferungen Pflicht und hier bewusst nicht
 * nachgebildet.
 */
export function validateVatId(value) {
    const vatId = normalizeVatId(value);
    if (vatId === '')
        return null;
    const prefix = vatId.slice(0, 2);
    const country = prefix === 'EL' ? 'GR' : prefix;
    const pattern = VAT_ID_PATTERNS[country];
    if (!pattern)
        return 'Der Laendercode der USt-IdNr. ist unbekannt.';
    if (!pattern.test(vatId))
        return `Die USt-IdNr. entspricht nicht dem Format fuer ${country}.`;
    if (country === 'DE' && !isValidGermanVatId(vatId)) {
        return 'Die deutsche USt-IdNr. ist nicht korrekt (Pruefziffer stimmt nicht).';
    }
    return null;
}
/** Pruefziffernverfahren des BZSt fuer deutsche USt-IdNr. (Modulo 11/10). */
function isValidGermanVatId(vatId) {
    const digits = vatId.slice(2).split('').map(Number);
    let product = 10;
    for (let i = 0; i < 8; i++) {
        let sum = (digits[i] + product) % 10;
        if (sum === 0)
            sum = 10;
        product = (2 * sum) % 11;
    }
    const checkDigit = (11 - product) % 10;
    return checkDigit === digits[8];
}
/**
 * Steuernummer des Finanzamts.
 *
 * Die Formate unterscheiden sich je Bundesland stark; geprueft wird daher
 * nur, dass ausschliesslich Ziffern und Trennzeichen vorkommen und die
 * Laenge plausibel ist.
 */
export function validateTaxNumber(value) {
    const taxNumber = value.trim();
    if (taxNumber === '')
        return null;
    if (!/^[\d/\s-]+$/.test(taxNumber)) {
        return 'Die Steuernummer darf nur Ziffern und Trennzeichen enthalten.';
    }
    const digits = taxNumber.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 13) {
        return 'Die Steuernummer hat eine unplausible Laenge (erwartet werden 10 bis 13 Ziffern).';
    }
    return null;
}
export function validateEmail(value) {
    const email = value.trim();
    if (email === '')
        return null;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)
        ? null
        : 'Die E-Mail-Adresse hat kein gueltiges Format.';
}
/** Deutsche Postleitzahl. */
export function validatePostalCode(value, country = 'DE') {
    const postalCode = value.trim();
    if (postalCode === '')
        return null;
    if (country !== 'DE')
        return null;
    return /^\d{5}$/.test(postalCode) ? null : 'Eine deutsche Postleitzahl hat fuenf Ziffern.';
}
//# sourceMappingURL=validation.js.map